import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/BikeRecommendation.css';
import aiService from '../services/aiService';

const BikeRecommendation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationSummary, setRecommendationSummary] = useState('');
  const [loading, setLoading] = useState(location.state?.isLoading || false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if formData is available, if not redirect to home
    if (!location.state || !location.state.formData) {
      navigate('/');
      return;
    }

    const formData = location.state.formData;
    generateRecommendations(formData);
    generateRecommendationSummary(formData);
  }, [location, navigate]);

  const generateRecommendations = async (formData) => {
    try {
      const results = await aiService.generateRecommendations(formData);
      setRecommendations(results);
      setLoading(false);
    } catch (err) {
      console.error('Error generating recommendations:', err);
      setError('Sorry, we encountered an error while generating your motorcycle recommendations. Please try again.');
      setLoading(false);
    }
  };

  const generateRecommendationSummary = async (formData) => {
    try {
      const summary = await aiService.generateRecommendationSummary(formData);
      setRecommendationSummary(summary);
    } catch (err) {
      console.error('Error generating recommendation summary:', err);
      setRecommendationSummary(
        "Based on your riding experience, preferences, and budget, I've carefully analyzed thousands of motorcycles to find these perfect matches for you. Each recommendation considers your specific riding style, technical requirements, and value for money to ensure you find a motorcycle that truly fits your needs."
      );
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className='recommendation-page loading-page'>
        <div className='overlay'></div>
        <div className='loading-container'>
          <img src='/motoGuruLogoMain.svg' alt='MotoGuru Logo' className='loading-logo' />
          <h2>Finding your perfect motorcycles...</h2>
          <p>Our AI is analyzing your preferences to recommend the best options for you.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='recommendation-page error-page'>
        <div className='overlay'></div>
        <div className='content'>
          <div className='card error-card'>
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button className='btn btn-primary' onClick={handleBack}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='recommendation-page'>
      <div className='overlay'></div>
      <div className='content'>
        <header>
          <h1>
            Your Perfect <span className='accent'>Motorcycle</span> Recommendations
          </h1>
          <p>Personalized motorcycle advice based on your experience, budget, and preferences</p>
        </header>

        <div className='recommendation-controls'>
          <div className='ai-recommendation-summary'>
            <div className='ai-icon'>
              <img src='/motoGuruLogoMain.svg' alt='MotoGuru Logo' className='ai-logo-image' />
            </div>
            <div className='ai-message'>
              <h3>MotoGuru AI's Recommendation</h3>
              <p>{recommendationSummary}</p>
            </div>
          </div>
        </div>

        <div className='recommendations-container'>
          {recommendations.length > 0 ? (
            recommendations.map((bike, index) => (
              <div className='bike-card' key={index}>
                <div className='bike-header'>
                  <h2>{bike.name}</h2>
                  <span className='bike-price'>₹{bike.price}</span>
                </div>

                <div className='bike-specs'>
                  <div className='spec'>
                    <span className='spec-label'>Engine</span>
                    <span className='spec-value'>{bike.displacement}</span>
                  </div>
                  <div className='spec'>
                    <span className='spec-label'>Power</span>
                    <span className='spec-value'>{bike.power}</span>
                  </div>
                </div>

                {bike.explanation && (
                  <div className='bike-explanation'>
                    <h3>
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                        <path d='M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z' />
                        <path d='m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z' />
                      </svg>
                      Why This Bike
                    </h3>
                    <p>{bike.explanation}</p>
                  </div>
                )}

                <div className='bike-features'>
                  <h3>
                    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                      <path d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.25-11.25v1.5a.75.75 0 0 1-1.5 0v-1.5a.75.75 0 0 1 1.5 0zm0 4v4.75a.75.75 0 0 1-1.5 0v-4.75a.75.75 0 0 1 1.5 0z' />
                    </svg>
                    Key Features
                  </h3>
                  <ul>
                    {bike.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className='bike-pros-cons'>
                  <div className='pros'>
                    <h3>Pros</h3>
                    <ul className='pros-list'>
                      {bike.pros &&
                        bike.pros.map((pro, idx) => (
                          <li key={idx} className='pro-item'>
                            {pro}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div className='cons'>
                    <h3>Cons</h3>
                    <ul className='cons-list'>
                      {bike.cons &&
                        bike.cons.map((con, idx) => (
                          <li key={idx} className='con-item'>
                            {con}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>

                <a href={bike.url} className='btn btn-primary bike-link' target='_blank' rel='noopener noreferrer'>
                  View Details
                  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
                    <path d='M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z' />
                    <path d='M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z' />
                  </svg>
                </a>
              </div>
            ))
          ) : (
            <div className='no-results'>
              <svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='currentColor' viewBox='0 0 16 16'>
                <path d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z' />
              </svg>
              <h3>No recommendations found</h3>
              <p>We couldn't find any motorcycles matching your criteria</p>
              <button className='btn btn-primary' onClick={handleBack}>
                Start New Search
              </button>
            </div>
          )}
        </div>

        <div className='action-buttons'>
          <button className='btn btn-primary' onClick={handleBack}>
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' viewBox='0 0 16 16'>
              <path d='M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z' />
            </svg>
            Start New Search
          </button>
        </div>
      </div>
    </div>
  );
};

export default BikeRecommendation;
